import { useRef, useState } from 'react';
import { NextSeo } from 'next-seo';
import SEO from 'next-seo.config';
import ResultsList from 'components/Results/ResultsList';
import RESULTS_CASES from 'data/results-cases';
import {
    RESULTS_DESCRIPTION,
    RESULTS_TITLE,
    aiOverviewCount,
    numberWord,
    sectorsOf,
} from 'utils/results';
import styles from 'styles/modules/Results.module.scss';

const EMAIL = 'editors@newspage.media';

const CopyEmail = () => {
    const [label, setLabel] = useState('Copy');
    const code = useRef(null);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            setLabel('Copied');
        } catch {
            const range = document.createRange();
            range.selectNodeContents(code.current);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            setLabel('Selected');
        }
        setTimeout(() => setLabel('Copy'), 2000);
    };

    return (
        <div className={styles.mail}>
            <code ref={code}>{EMAIL}</code>
            <button type="button" onClick={copy}>
                {label}
            </button>
        </div>
    );
};

const Results = ({ cases }) => {
    const url = `${process.env.HOST}/results`;

    return (
        <div className={styles.page}>
            <NextSeo
                {...SEO}
                title={RESULTS_TITLE}
                titleTemplate="%s"
                description={RESULTS_DESCRIPTION}
                canonical={url}
                openGraph={{
                    ...SEO.openGraph,
                    title: RESULTS_TITLE,
                    description: RESULTS_DESCRIPTION,
                    url,
                }}
            />
            <div className={styles.wrap}>
                <p className={styles.kick}>Results</p>
                <h1>Real searches. Real results. Dated.</h1>
                <p className={styles.lede}>
                    Every example below is a <b>Newspage story</b>, or a{' '}
                    <b>sponsor&apos;s own page</b> linked from one, appearing on
                    page one of Google or in Google&apos;s AI Overview for a
                    search people actually make. Each one was seen on Google UK
                    by the Newspage team and logged with the date and time.
                </p>
                <div className={styles.facts} aria-label="Summary">
                    <div>
                        <strong>{cases.length}</strong>
                        <span>
                            examples across{' '}
                            {numberWord(sectorsOf(cases).length)} sectors
                        </span>
                    </div>
                    <div>
                        <strong>{aiOverviewCount(cases)}</strong>
                        <span>cited in Google&apos;s AI Overview</span>
                    </div>
                    <div>
                        <strong>42 min</strong>
                        <span>fastest, from publication to page one</span>
                    </div>
                </div>
                <p className={styles.how}>
                    How to read these: each result is what Google showed at the
                    time stated, checked in a private window. Search results
                    move, especially for news, so a position on one day is not a
                    promise for the next. The screenshots are held by Newspage
                    and available on request.
                </p>

                <ResultsList cases={cases} />

                <section className={styles.cta}>
                    <h2>Want this for your search?</h2>
                    <p>
                        Sponsor a Newspage alert or story and the story links to
                        your page for the search you choose. We can also give
                        your page instructions so Google and the AI Overview can
                        read it properly. Newspage&apos;s developers can make
                        the changes for you if needed.
                    </p>
                    <CopyEmail />
                </section>
                <p className={styles.footnote}>
                    Newspage. Be trusted. Be visible. Be the story. &middot;
                    Results logged by Newspage, October to November 2026.
                </p>
            </div>
        </div>
    );
};

export const getStaticProps = () => ({ props: { cases: RESULTS_CASES } });

export default Results;
